import {ViewportScroller} from '@angular/common';
import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {debounceTime, of, Subject, switchMap} from 'rxjs';
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {environment} from "@environment";
import {Category, CategoryItem, Gender, Lookups, TOAST_STATE} from "@model";
import {CategoriesService, Features, ToastService} from "@services";

@Component({
    selector: 'app-categories-list',
    templateUrl: './categories-list.component.html',
    styleUrls: ['./categories-list.component.scss'],
    standalone: false
})
export class CategoriesListComponent extends AbstractComponent implements OnInit {

  @Input() categoryTypes: Lookups[] = [];

  selectedCategoryType: number | undefined;
  hideCategoryForm: boolean = true;
  allCategories: Category[] = [];
  currentCategory: Category | undefined;
  selectedCategoriesList: Category[] = [...this.allCategories];
  organiserId: number | undefined;
  searchQuery = new Subject();
  searchText: string = "";

  constructor(public translocoService: TranslocoService,
              public categoriesService: CategoriesService,
              private scroller: ViewportScroller,
              private route: ActivatedRoute,
              private modalService: NgbModal,
              private toastService: ToastService,
  ) {
    super();
  }

  ngOnInit() {
    const feature: string | undefined = this.route.routeConfig?.data?.['feature'];
    if (feature === Features.OrganisersEdit) {
      this.organiserId = +this.route.snapshot.queryParams['organiserId'];
    }
    this.subscribeToCurrentCategories();
    this.subscribeToRouteDataChange();
    this.subscribeToSearchResult();
  }

  getItemsMale(category: Category): CategoryItem[] {
    return category.items?.filter((item: CategoryItem) => item.gender === Gender.MALE) ?? []
  }

  getItemsFemale(category: Category): CategoryItem[] {
    return category.items?.filter((item: CategoryItem) => item.gender === Gender.FEMALE) ?? []

  }

  filterCategories(): Category[] {
    return this.allCategories.filter((category: Category) => {
      if (this.searchText) {
        if (!category.name?.toLowerCase().includes(this.searchText.toLowerCase())) return false;
      }
      if (this.selectedCategoryType) {
        if (category.categoryType !== this.selectedCategoryType) return false;
      }

      return true;
    })
  }

  handleInput(value: string) {
    this.searchText = value;
    if (this.searchText.length > 1) {
      this.searchQuery.next(this.searchText.trim());
    } else {
      this.selectedCategoriesList = this.filterCategories()
    }
  }

  private subscribeToSearchResult(): void {
    this.subscriptions.push(this.searchQuery.pipe(
      debounceTime(environment.fullTextSearchDelay), switchMap(() => {
          return of(this.filterCategories());
        }
      )
    ).subscribe((value: Category[]) => {
      this.selectedCategoriesList = value;
    }));
  }

  private subscribeToCurrentCategories(): void {
    this.subscriptions.push(
      this.categoriesService.currentCategories.subscribe((value: Category[] | undefined) => {
        this.allCategories = value ?? [];
        this.selectedCategoriesList = [...this.allCategories];
        this.selectedCategoryType = undefined;
      })
    )
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({categories}: Data) => {
        this.allCategories = categories ?? [];
        this.selectedCategoriesList = [...this.allCategories];
      })
    )
  }

  trackByFn(index: number, item: Category): number | undefined {
    return item.id;
  }

  onCategorySelect(categoryType: number | undefined): void {
    if (this.selectedCategoryType === categoryType || !categoryType) {
      this.selectedCategoryType = undefined;
    } else {
      this.selectedCategoryType = categoryType;
    }
    this.selectedCategoriesList = this.filterCategories();
  }

  navigateToCategoryForm() {
    this.scroller.scrollToAnchor("categoryForm");
  }

  toggleCategoryForm() {
    this.hideCategoryForm = !this.hideCategoryForm;
  }


  onEditCategory(category: Category) {
    if (category.organiserId) {
      this.hideCategoryForm = false;
      this.currentCategory = category;
      this.navigateToCategoryForm();
    }
  }

  onDeleteCategory(category: Category) {
    if (category.organiserId) {
      const modalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
      });

      modalRef.componentInstance.title = this.translocoService.translate("administrator.categories.delete.dialog.title");
      modalRef.componentInstance.text = this.translocoService.translate("administrator.categories.delete.dialog.text");

      modalRef.result.then((_) => {
        this.categoriesService.deleteCategory(this.organiserId, category, () => {
          this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.category.delete"))
          this.onCancel();
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          this.onCancel();
        });
      }, (_) => {
        /// ignore
      });
    }
  }
  onCancel() {
    this.currentCategory = undefined;
    this.hideCategoryForm = true;
  }

}
